import {
	PropsWithChildren,
	ReactElement,
	useMemo
} from "react";
import classNames from "classnames";
import { Portal } from "../Portal/Portal";
import { setTrackingId } from "client/Misc/utils";
import styles from "./RightSlider.module.less";
import { IconButton } from "../IconButton/IconButton";
import { SegmentedControl, SegmentedControlProps } from "../SegmentedControl/SegmentedControl";
import { FontIconName } from "../Icon/Icon";

export type RightSliderProps = {
	isPinned?: boolean;
	trackingId?: string;
	hasNoPadding?: boolean;
};

export const RightSlider = (props: PropsWithChildren<RightSliderProps>): ReactElement => {
	const {
		children,
		isPinned = false,
		trackingId,
		hasNoPadding = false
	} = props;

	const host = useMemo(
		(): HTMLElement => {
			const element: HTMLElement | null = document.querySelector<HTMLElement>(".app-layout__columns");
			if (!element) {
				throw new Error("Host for right slider is not found");
			}

			return element;
		},
		[]
	);

	return (
		<Portal container={host}>
			<div
				{...setTrackingId(trackingId)}
				className={classNames(
					styles.root,
					{
						[styles.root_pinned]: isPinned,
						[styles.root_noPadding]: hasNoPadding
					}
				)}
			>
				{children}
			</div>
		</Portal>
	);
};
RightSlider.displayName = "RightSlider";

const RightSliderBody = (props: PropsWithChildren<{}>): ReactElement => {
	return (
		<div className={styles.body}>
			{props.children}
		</div>
	);
};
RightSliderBody.displayName = "RightSlider.Body";

type RightSliderSegmentedControlProps<TValue> = SegmentedControlProps<TValue>;

const RightSliderSegmentedControl = <TValue, >(props: RightSliderSegmentedControlProps<TValue>): ReactElement => {
	return (
		<div className={styles.segmentedControlContainer}>
			<SegmentedControl<TValue>
				{...props}
			/>
		</div>
	);
};
RightSliderSegmentedControl.displayName = "RightSlider.SegmentedControl";

export type RightSliderHeaderAction = {
	icon: FontIconName;
	onClick: () => void;
	trackingId: string;
};

type RightSliderHeaderProps = {
	title: string;
	customActions?: readonly RightSliderHeaderAction[];
	onClose?: () => void;
} & ({
	isPinnable?: false;
} | {
	isPinnable: true;
	isPinned: boolean;
	onIsPinnedChange: (isPinned: boolean) => void;
});

const RightSliderHeader = (props: RightSliderHeaderProps): ReactElement => {
	const {
		title,
		customActions,
		onClose
	} = props;

	return (
		<div className={styles.header}>
			<h3 className={styles.title}>{title}</h3>
			{props.isPinnable && (
				<IconButton
					kind="transparent-colored"
					icon={{
						kind: "font",
						name:"thumbtack",
						color: props.isPinned ? "primary" : undefined
					}}
					onClick={() => props.onIsPinnedChange(!props.isPinned)}
					shape="round"
					trackingId="pin-button"
				/>
			)}
			{customActions && customActions.map(
				x => (
					<IconButton
						key={x.trackingId}
						kind="transparent"
						icon={x.icon}
						onClick={x.onClick}
						shape="round"
						trackingId={x.trackingId}
					/>
				)
			)}
			{onClose && (
				<IconButton
					icon="xmark"
					kind="transparent"
					onClick={onClose}
					shape="round"
					trackingId="close-button"
				/>
			)}
		</div>
	);
};
RightSliderHeader.displayName = "RightSlider.Header";

RightSlider.Header = RightSliderHeader;
RightSlider.Body = RightSliderBody;
RightSlider.SegmentControl = RightSliderSegmentedControl;
